export type CategorySlugInput = {
  slug?: string | null
}

const INVALID_SLUG_LITERALS = new Set(['', 'undefined', 'null', 'nan'])

/**
 * Returns a normalized category slug or null when unusable.
 * Rejects empty values and accidental literals like "undefined"/"null".
 */
export function normalizeCategorySlug(raw: unknown): string | null {
  if (raw == null) return null
  const trimmed = String(raw).trim()
  if (!trimmed) return null

  const noSlashes = trimmed.replace(/^\/+|\/+$/g, '')
  if (!noSlashes || noSlashes.includes('/')) return null

  const lower = noSlashes.toLowerCase()
  if (INVALID_SLUG_LITERALS.has(lower)) return null

  return noSlashes
}

export function hasValidCategorySlug(category?: CategorySlugInput): boolean {
  return normalizeCategorySlug(category?.slug) != null
}

/**
 * Single source of truth for category URLs in storefront.
 * Invalid category input always falls back to `/shop`.
 */
export function buildCategoryUrl(category?: CategorySlugInput): string {
  const slug = normalizeCategorySlug(category?.slug)
  if (!slug) return '/shop'
  return `/category/${encodeURIComponent(slug)}`
}

/**
 * Hardens raw path links targeting category routes.
 * Returns normalized category path or `/shop` for invalid variants.
 * Returns null when path is unrelated to category routes.
 */
export function normalizeCategoryPathOrShop(pathOnly: string): string | null {
  const clean = String(pathOnly ?? '').trim()
  if (!clean) return null

  const collapsed = clean.replace(/\/{2,}/g, '/')
  const lower = collapsed.toLowerCase()

  if (lower === '/category' || lower === '/category/') return '/shop'
  if (!lower.startsWith('/category/')) return null

  const segs = collapsed.split('/').filter(Boolean)
  // expected: ["category", "<slug>"] only
  if (segs.length !== 2) return '/shop'

  return buildCategoryUrl({ slug: segs[1] })
}
